import { useApolloClient } from '@apollo/client'
import React, { FC, ReactNode, useContext, useEffect, useState } from 'react'

import { NativeModal, NativeModalBody, NativeModalFooter, NativeModalHeader } from '@Components/Modal/NativeModal/NativeModal'
import { ProductContext } from '@Components/Product/Context'
import { Button } from '@CoreComponents/Buttons/Button'
import { BlockTypeWithGlobal, Page, PagePresentation, PageType, Product } from '@Generated/graphql'
import { enableBodyScroll } from '@Globals/BodyHelpers'
import { getProductFieldValue } from '@Globals/CMS/FieldHelpers'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { useTranslation } from '@Globals/Translations'
import { getSinglePostByID } from '@Queries/cms/SinglePost.query'
import { getSingleProduct } from '@Queries/cms/SingleProduct.query'

export function useModalComponents(type: PageType, id: string, presentation: PagePresentation = PagePresentation.Modal) {
  const productFallback = useContext(ProductContext)
  const [product, setProduct] = useState<Product | null>(null)
  const [components, setComponents] = useState<BlockTypeWithGlobal[]>([])
  const client = useApolloClient()

  useEffect(() => {
    (async () => {
      if (type === PageType.Product) {
        const { data } = await getSingleProduct(client, '', id, 0, undefined, presentation)

        if (data.page?.blocks) {
          setComponents(data.page.blocks as BlockTypeWithGlobal[])
          setProduct(getProductFieldValue((data.page as Page)?.fields || [], 'product'))
        }
        return
      }
      const { data } = await getSinglePostByID(client, type, id, presentation)

      if (data.page?.blocks) {
        setComponents(data.page.blocks as BlockTypeWithGlobal[])
        setProduct(null)
      }
    })()
  }, [client, id, presentation, type])

  return (
    <ProductContext.Provider value={product || productFallback}>
      {useBlockComponents(components, '', false, presentation)}
    </ProductContext.Provider>
  )
}

interface CMSModalContentProps {
  id: string
  onClose: () => void
  presentation?: PagePresentation
  type: PageType
}

export const CMSModalContent: FC<CMSModalContentProps> = ({ id, onClose, presentation = PagePresentation.Modal, type }) => {
  const { t } = useTranslation()
  let customHeader: null | ReactNode = null
  let customFooter: null | ReactNode = null
  let presentationOverride = presentation
  if (type === PageType.Modal && id === '20') {
    presentationOverride = PagePresentation.Sidebar
    customHeader = `${t('header:SHIPPING_OPTIONS')}`
    customFooter = (
      <Button
        innerSpacing="medium"
        onClick={() => {
          onClose()
          enableBodyScroll()
        }}
        style={{ width: '100%', margin: '14px auto' }}
        theme="black"
      >
        {t('common:CLOSE')}
      </Button>
    )
  }

  const modalComponents = useModalComponents(type, id, presentationOverride)
  if (presentationOverride === PagePresentation.Sidebar) {
    return (
      <NativeModal alignment="right" backdrop closeOnBackDrop defaultOpen onClose={onClose} prerender>
        <NativeModalHeader>
          {customHeader}
        </NativeModalHeader>
        <NativeModalBody>
          {modalComponents}
        </NativeModalBody>
        {customFooter && (
          <NativeModalFooter>
            {customFooter}
          </NativeModalFooter>
        )}
      </NativeModal>
    )
  }

  return (
    <NativeModal backdrop closeOnBackDrop defaultOpen onClose={onClose}>
      <NativeModalHeader />
      <NativeModalBody>
        {modalComponents}
      </NativeModalBody>
    </NativeModal>
  )
}
